import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Button,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    SelectChangeEvent,
    TextField,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from '@mui/material';
import { Delete, Add } from '@mui/icons-material';
import { getStoresByInstitutionId } from '../../services/store.service';
import { Store } from '../../interfaces/store.interface';
import { ItemService } from '../../services/item.service';
import { Item } from '../../interfaces/item.interface';
import { StockTransferService } from '../../services/stockTransfer.service';
import { StockTransferLineCreateDTO } from '../../interfaces/stockTransfer.interface';
import { AuthService } from '../../services/auth.service';
import { useToast } from '../../context/ToastContext';

interface LineInput {
    itemId: string;
    quantity: string;
}

const StockTransferCreate: React.FC = () => {
    const navigate = useNavigate();
    const toast = useToast();
    const [stores, setStores] = useState<Store[]>([]);
    const [items, setItems] = useState<Item[]>([]);
    const [fromStoreId, setFromStoreId] = useState<string>('');
    const [toStoreId, setToStoreId] = useState<string>('');
    const [transferDate, setTransferDate] = useState<string>('');
    const [notes, setNotes] = useState<string>('');
    const [lines, setLines] = useState<LineInput[]>([{ itemId: '', quantity: '' }]);
    const [submitting, setSubmitting] = useState<boolean>(false);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [storeResponse, itemResponse] = await Promise.all([
                    getStoresByInstitutionId(0, 100),
                    ItemService.getItemsByInstitution(0, 200),
                ]);
                setStores(storeResponse.content);
                setItems(itemResponse.content);
            } catch (err) {
                console.error('Error loading stores/items:', err);
                toast.showApiError(err, 'Failed to load stores or items');
            }
        };
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleFromStoreChange = (event: SelectChangeEvent) => {
        setFromStoreId(event.target.value);
    };

    const handleToStoreChange = (event: SelectChangeEvent) => {
        setToStoreId(event.target.value);
    };

    const handleLineChange = (index: number, field: keyof LineInput, value: string) => {
        setLines((prev) =>
            prev.map((line, i) => (i === index ? { ...line, [field]: value } : line)),
        );
    };

    const handleAddLine = () => {
        setLines((prev) => [...prev, { itemId: '', quantity: '' }]);
    };

    const handleRemoveLine = (index: number) => {
        setLines((prev) => prev.filter((_, i) => i !== index));
    };

    const handleSubmit = async () => {
        const institutionId = AuthService.getSelectedInstitution();
        if (!institutionId) {
            toast.showError('No institution selected');
            return;
        }
        if (!fromStoreId) {
            toast.showError('Please select a source store');
            return;
        }
        if (!toStoreId) {
            toast.showError('Please select a destination store');
            return;
        }
        if (fromStoreId === toStoreId) {
            toast.showError('Source and destination stores must be different');
            return;
        }

        const payloadLines: StockTransferLineCreateDTO[] = [];
        for (const line of lines) {
            if (!line.itemId && line.quantity.trim() === '') continue;
            if (!line.itemId) {
                toast.showError('Please select an item for every line');
                return;
            }
            const qty = Number(line.quantity);
            if (line.quantity.trim() === '' || Number.isNaN(qty) || qty <= 0) {
                toast.showError('Enter a valid positive quantity for every line');
                return;
            }
            payloadLines.push({ itemId: line.itemId, quantity: qty });
        }
        if (payloadLines.length === 0) {
            toast.showError('Add at least one transfer line');
            return;
        }

        try {
            setSubmitting(true);
            const created = await StockTransferService.createStockTransfer({
                fromStoreId,
                toStoreId,
                institutionId,
                transferDate: transferDate || undefined,
                notes: notes.trim() || undefined,
                lines: payloadLines,
            });
            toast.showSuccess('Stock transfer created');
            navigate(`/stock-transfers/${created.id}`);
        } catch (err) {
            console.error('Error creating stock transfer:', err);
            toast.showApiError(err, 'Failed to create stock transfer');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h4" sx={{ mb: 3 }}>
                New Stock Transfer
            </Typography>
            <Paper elevation={3} sx={{ p: 3, mb: 3, maxWidth: 720 }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 3 }}>
                    <FormControl sx={{ minWidth: 240, flex: 1 }}>
                        <InputLabel>From Store</InputLabel>
                        <Select value={fromStoreId} label="From Store" onChange={handleFromStoreChange}>
                            {stores.map((store) => (
                                <MenuItem key={store.id} value={store.id}>
                                    {store.storeName}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl sx={{ minWidth: 240, flex: 1 }}>
                        <InputLabel>To Store</InputLabel>
                        <Select value={toStoreId} label="To Store" onChange={handleToStoreChange}>
                            {stores.map((store) => (
                                <MenuItem key={store.id} value={store.id}>
                                    {store.storeName}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>

                <TextField
                    label="Transfer Date"
                    type="date"
                    fullWidth
                    value={transferDate}
                    onChange={(e) => setTransferDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{ mb: 3 }}
                />

                <TextField
                    label="Notes"
                    fullWidth
                    multiline
                    minRows={3}
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    sx={{ mb: 1 }}
                />
            </Paper>

            <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">Transfer Lines</Typography>
                    <Button startIcon={<Add />} onClick={handleAddLine}>
                        Add Line
                    </Button>
                </Box>
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                                <TableCell>#</TableCell>
                                <TableCell>Item</TableCell>
                                <TableCell align="right">Quantity</TableCell>
                                <TableCell align="center">Remove</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {lines.map((line, index) => (
                                <TableRow key={index}>
                                    <TableCell>{index + 1}</TableCell>
                                    <TableCell>
                                        <FormControl size="small" sx={{ minWidth: 240 }}>
                                            <Select
                                                displayEmpty
                                                value={line.itemId}
                                                onChange={(e) =>
                                                    handleLineChange(index, 'itemId', e.target.value)
                                                }
                                            >
                                                <MenuItem value="">
                                                    <em>Select item</em>
                                                </MenuItem>
                                                {items.map((item) => (
                                                    <MenuItem key={item.id} value={item.id}>
                                                        {item.itemCode} - {item.itemName}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </TableCell>
                                    <TableCell align="right">
                                        <TextField
                                            size="small"
                                            type="number"
                                            value={line.quantity}
                                            onChange={(e) =>
                                                handleLineChange(index, 'quantity', e.target.value)
                                            }
                                            sx={{ width: 120 }}
                                        />
                                    </TableCell>
                                    <TableCell align="center">
                                        <IconButton
                                            size="small"
                                            color="error"
                                            onClick={() => handleRemoveLine(index)}
                                            disabled={lines.length === 1}
                                            aria-label="Remove line"
                                        >
                                            <Delete />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={submitting || !fromStoreId || !toStoreId}
                >
                    {submitting ? 'Creating...' : 'Create Transfer'}
                </Button>
                <Button variant="outlined" onClick={() => navigate('/stock-transfers')}>
                    Cancel
                </Button>
            </Box>
        </Box>
    );
};

export default StockTransferCreate;
